import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import { Icon } from "@mui/material";
import { SearchOutlined } from "@ant-design/icons";
import { useHotkeys } from "react-hotkeys-hook";
import { useGetMenu } from "api/menu";

type SearchItem = {
  id: number;
  title: string;
  url: string;
  children?: SearchItem[];
};

const Search: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [combinedData, setCombinedData] = useState<SearchItem[]>([]);
  const [filteredData, setFilteredData] = useState<SearchItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const navigate = useNavigate();

  const { menuLoading, menu } = useGetMenu();

  const transformMenuData = useCallback((menuChildren: any[]): SearchItem[] => {
    const result: SearchItem[] = [];

    const processChildren = (children: any[]) => {
      children.forEach((child) => {
        if (child.url !== null) {
          result.push({
            id: child.id,
            title: child.title,
            url: child.url,
          });
        }

        if (child.children) {
          processChildren(child.children);
        }
      });
    };

    processChildren(menuChildren);
    return result;
  }, []);

  useEffect(() => {
    if (menu && menu.children) {
      const menuData = transformMenuData(menu.children);
      setCombinedData(menuData);
      setFilteredData(menuData);
    }
  }, [menu, transformMenuData]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSearchText("");
    setFilteredData(combinedData);
    setSelectedIndex(null);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();
    setSearchText(query);
    const filtered = combinedData.filter(
      (item) =>
        item.title.toLowerCase().includes(query) ||
        (item.children &&
          item.children.some((child) =>
            child.title.toLowerCase().includes(query)
          ))
    );
    setFilteredData(filtered);
    setSelectedIndex(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowDown") {
      setSelectedIndex((prev) => {
        const newIndex =
          prev === null ? 0 : Math.min(prev + 1, filteredData.length - 1);
        scrollToItem(newIndex);
        return newIndex;
      });
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setSelectedIndex((prev) => {
        const newIndex =
          prev === null ? filteredData.length - 1 : Math.max(prev - 1, 0);
        scrollToItem(newIndex);
        return newIndex;
      });
      e.preventDefault();
    } else if (e.key === "Enter" && selectedIndex !== null) {
      navigate(filteredData[selectedIndex].url);
      handleClose();
    }
  };

  const scrollToItem = (index: number) => {
    if (listRef.current && index >= 0 && index < filteredData.length) {
      const item = listRef.current.children[index] as HTMLLIElement;
      item.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    }
  };

  const formatTitle = (title: string) => {
    return title.replace(/([a-z])([A-Z])/g, "$1 $2");
  };

  useHotkeys("ctrl+Q", (event) => {
    event.preventDefault();
    handleOpen();
  });

  return (
    <Box sx={{ width: "100%", ml: { xs: 0, md: 1 } }}>
      <FormControl sx={{ width: { xs: "100%", md: 224 } }} onClick={handleOpen}>
        <TextField
          placeholder="Search..."
          variant="outlined"
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
                >
                  Ctrl + Q
                </Typography>
              </InputAdornment>
            ),
            startAdornment: (
              <Icon>
                <SearchOutlined />
              </Icon>
            ),
          }}
        />
      </FormControl>
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "50%",
            maxHeight: "50vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            overflow: "hidden",
            p: 3,
          }}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          <Box
            sx={{
              position: "sticky",
              top: 0,
              bgcolor: "background.paper",
              zIndex: 1,
              p: 2,
              borderBottom: "1px solid #ddd",
            }}
          >
            <Typography variant="h4" sx={{ mb: 2 }}>
              Search
            </Typography>
            <TextField
              value={searchText}
              onChange={handleSearch}
              placeholder="Type to search..."
              variant="outlined"
              fullWidth
              inputRef={searchInputRef}
              InputProps={{
                startAdornment: (
                  <Icon>
                    <SearchOutlined />
                  </Icon>
                ),
              }}
            />
          </Box>
          {menuLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "40vh",
              }}
            >
              <CircularProgress />
            </Box>
          ) : filteredData.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "10vh",
              }}
            >
              <Typography variant="h6">
                No results found for '{searchText}'
              </Typography>
            </Box>
          ) : (
            <List
              ref={listRef}
              sx={{
                marginTop: "2vh",
                maxHeight: "40vh",
                overflowY: "auto",
                "&::-webkit-scrollbar": { display: "none" },
                msOverflowStyle: "none",
                scrollbarWidth: "none",
              }}
            >
              {filteredData.map((item, index) => (
                <ListItem
                  key={item.id}
                  onClick={() => {
                    navigate(item.url);
                    handleClose();
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                  sx={{
                    bgcolor:
                      selectedIndex === index ? "action.selected" : "inherit",
                    cursor: "pointer",
                    borderRadius: "8px",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                    px: 2,
                  }}
                >
                  <ListItemText
                    primary={formatTitle(item.title)}
                    primaryTypographyProps={{
                      fontWeight: selectedIndex === index ? "bold" : "normal",
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default Search;
